// MarkdownRenderer.tsx
import React, { useEffect, useState } from 'react';
import MarkdownItAsync from 'markdown-it-async';
import { fromAsyncCodeToHtml } from '@shikijs/markdown-it/async';
import { codeToHtml } from 'shiki';

/**
 * shiki bundle'ında olmayan bir dil (ör. ```pseudocode, ```çıktı) gelirse
 * codeToHtml ShikiError fırlatır ve tüm render reject olur -> içerik hiç
 * görünmez. Bu sarmalayıcı bilinmeyen dili 'text'e düşürerek bloğu yine de
 * (highlight olmadan) basar.
 */
const safeCodeToHtml: typeof codeToHtml = async (code, options) => {
  try {
    return await codeToHtml(code, options);
  } catch {
    return await codeToHtml(code, { ...options, lang: 'text' });
  }
};

const escapeHtml = (s: string) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

interface MarkdownRendererProps {
  content: string;
  theme?: 'ayu-dark' | 'vitesse-dark';
  /**
   * Ham HTML etiketlerine izin ver. AI geri bildirimi gibi içeriklerde
   * `false` verilmeli; aksi halde metindeki <p>, <span> gibi etiketler
   * gerçek DOM elementi olarak parse edilip ekrandan kaybolur.
   */
  allowHtml?: boolean;
  /** Tek satır sonlarını <br>'e çevir (AI geri bildirimi için okunaklı). */
  breaks?: boolean;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  theme = 'ayu-dark',
  allowHtml = true,
  breaks = false,
}) => {
  const [html, setHtml] = useState<string>('');

  useEffect(() => {
    const renderMarkdown = async () => {
      const md = MarkdownItAsync({
        html: allowHtml,
        breaks,
      });

      // Linkleri yeni sekmede aç
      md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
        const token = tokens[idx];
        if (token.attrIndex('target') < 0) {
          token.attrPush(['target', '_blank']);
        }
        if (token.attrIndex('rel') < 0) {
          token.attrPush(['rel', 'noopener noreferrer']);
        }
        return self.renderToken(tokens, idx, options);
      };

      md.use(
        fromAsyncCodeToHtml(safeCodeToHtml, {
          themes: {
            light: 'ayu-dark',
            dark: 'ayu-dark',
          },
        })
      );

      try {
        const rendered = await md.renderAsync(content);
        setHtml(rendered);
      } catch (err) {
        // Markdown/shiki render beklenmedik şekilde patlarsa içeriği boş
        // bırakma; en azından escape'lenmiş düz metin olarak göster.
        console.error('MarkdownRenderer render error:', err);
        setHtml(
          `<div style="white-space:pre-wrap">${escapeHtml(content)}</div>`
        );
      }
    };

    renderMarkdown();
  }, [content, theme, allowHtml, breaks]);

  return (
    <div
      className="prose prose-invert max-w-none"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};
